import React, { useState, useEffect, useRef } from "react";
import AuthUserContext from "./context";
import { useFirebase } from "../Firebase";
import { getQueryArgs } from "../../helpFunctions/general";
import * as Sentry from "@sentry/react";
import { useLocation } from "react-router-dom";
import * as ROUTES from "../../constants/routes";
import { useNavigateCompat } from "../../helpFunctions/hooks";

const withAuthentication = Component => {
  const WithAuthentication = props => {
    const [authUser, setAuthUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [signingIn, setSigningIn] = useState(false);
    const readyUidRef = useRef(null);
    const pendingUserUpdateRef = useRef(null);

    const navigate = useNavigateCompat();
    const location = useLocation();
    const firebase = useFirebase();

    const updateUser = user => {
      const uid = firebase.auth.currentUser?.uid;
      if (!uid) return;
      if (readyUidRef.current !== uid) {
        // NOTE: Registration can update claims before the database profile finishes loading.
        // A login flow can also call updateUser from a render that captured authUser as null
        // after the profile loads, so use the ref rather than that render's authUser.
        // Keep early patches off authUser until the Firebase Auth UID is present in context.
        const pending = pendingUserUpdateRef.current;
        pendingUserUpdateRef.current = {
          uid,
          user: { ...(pending?.uid === uid ? pending.user : {}), ...user },
        };
        return;
      }
      setAuthUser(prevAuthUser =>
        prevAuthUser?.userId === uid ? { ...prevAuthUser, ...user, userId: uid } : prevAuthUser
      );
    };

    useEffect(() => {
      const span = Sentry.startInactiveSpan({ name: "Firebase Auth", op: "db" });
      // Start listening to firebase auth updates (once on mount)
      const listener = firebase.onAuthUserListener(
        authUser => {
          const uid = firebase.auth.currentUser?.uid;
          if (!uid || authUser?.userId !== uid) return;
          const pending = pendingUserUpdateRef.current;
          pendingUserUpdateRef.current = null;
          const nextAuthUser =
            pending?.uid === uid
              ? {
                  ...authUser,
                  ...pending.user,
                  claims: pending.user.claims ? { ...authUser.claims, ...pending.user.claims } : authUser.claims,
                  userId: uid,
                }
              : authUser;
          readyUidRef.current = uid;
          setAuthUser(nextAuthUser);
          setLoading(false);
          span.end();
          if (!nextAuthUser?.userId) {
            Sentry.setUser(null);
          } else {
            // Only report email for internal users for GDPR compliance
            const email = nextAuthUser.email?.endsWith("fictivereality.com") ? nextAuthUser.email : null;
            Sentry.setUser({
              id: nextAuthUser.userId,
              memberOf: nextAuthUser.claims?.memberOf,
              emailVerified: nextAuthUser.claims?.email_verified,
              ip_address: null,
              email: email,
            });
          }
          console.log(`Authenticated as ${uid}`);
        },
        () => {
          readyUidRef.current = null;
          pendingUserUpdateRef.current = null;
          span.end();
          Sentry.setUser(null);
          setAuthUser(null);
          setLoading(false);
        }
      );

      return () => {
        readyUidRef.current = null;
        pendingUserUpdateRef.current = null;
        listener();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [firebase]);

    useEffect(() => {
      // If invite query parameter at initial load, redirect
      const queryArgs = getQueryArgs(location);
      const invite = queryArgs?.get("invite");
      if (invite) {
        navigate(
          {
            pathname: ROUTES.SIGN_IN,
            search: location.search,
          },
          {
            // nextUrl includes the invite query parameter but it will be removed at signin page
            state: { nextUrl: `${location.pathname}${location.search}` },
          }
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <AuthUserContext.Provider value={{ authUser, loading, signingIn, updateUser, setSigningIn }}>
        <Component {...props} authUser={loading || signingIn ? null : authUser} />
      </AuthUserContext.Provider>
    );
  };

  return WithAuthentication;
};

export default withAuthentication;
